import { useState } from 'react';
import { SideDrawer } from '../side-drawer/SideDrawer';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import './FaqModal.scss';
import faqs from './faq.json';
import { useAdobeActions } from '../../hooks/useAnalyticsUIEvents';
import { useChrLdFlag } from '@chr/common-launchdarkly';

export interface FaqModalProps {
  open: boolean;
  handleClose: (event: object) => void;
}

export const FaqModal = ({ open, handleClose }: FaqModalProps) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const theme = useTheme();
  const translate = useTranslation();
  const { handleAdobeAction } = useAdobeActions();
  const shouldShowReferenceNumberFaq = useChrLdFlag('enhanced-externaltracking-search', false);
  const shouldShowUpdatedReferenceNumberFaq = useChrLdFlag('enhanced-externaltracking-search-v2', false);
  const filteredFaqs = (shouldShowReferenceNumberFaq ? faqs : faqs.filter((faq) => faq.id !== 3)).map((faq) =>
    faq.id === 3 && faq.listV2
      ? {
          ...faq,
          list: shouldShowUpdatedReferenceNumberFaq ? faq.listV2 : faq.list,
        }
      : faq
  );

  const onClose = (event: object) => {
    handleClose(event);
  };

  const handleChange =
    (panel: string, question: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      handleAdobeAction('accordion', 'click', question);
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <SideDrawer open={open} handleClose={onClose} title={translate('FAQ')}>
      {filteredFaqs.map((faq) => (
        <Accordion
          id={`accordion-panel-${faq.id}`}
          key={`accordion-panel-${faq.id}`}
          expanded={expanded === `accordion-panel-${faq.id}`}
          onChange={handleChange(`accordion-panel-${faq.id}`, faq.question)}
          disableGutters
          square
          elevation={0}
          sx={{
            '&:before': {
              display: 'none',
            },
            padding: '16px 0',
            borderBottom: 1,
            borderColor: 'dividers.mediumEmphasis',
          }}
        >
          <AccordionSummary
            data-testid={`question-${faq.id}`}
            id={`accordion-content-${faq.id}`}
            key={`accordion-content-${faq.id}`}
            aria-controls={`content-${faq.id}`}
            expandIcon={
              <Icon sx={{ color: 'text.secondary', fontSize: '24px' }}>
                expand_more
              </Icon>
            }
            sx={{
              padding: 0,
              margin: '0 8px 0 0',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: `${theme.palette.grey[900]}`,
              }}
            >
              {translate(faq.question)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={`accordion-detail-${faq.id}`}
            key={`accordion-detail-${faq.id}`}
            data-testid={`answer-${faq.id}`}
            sx={{
              padding: '8px 0 0',
            }}
          >
            <Typography variant="body1">{translate(faq.answer)}</Typography>
            {faq.eg.map((example) => (
              <Typography key={`${faq.id}-${example}`} variant="body1">{translate(example)}</Typography>
            ))}
            {faq.subtitle && (
              <Typography variant="body1" sx={{ mt: 2 }}>
                {translate(faq.subtitle)}
              </Typography>
            )}
            {faq.list && (
              <ul style={{ margin: 0, paddingLeft: '24px' }}>
                {faq.list.map((item) => (
                  <li key={item}>
                    <Typography variant="body1">{translate(item)}</Typography>
                  </li>
                ))}
              </ul>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </SideDrawer>
  );
};
